import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { logout } from '../../features/auth/authSlice';
import Icon from '../AppIcon';
import Button from './Button';
import LanguageSwitcher from '../../pages/i18n/LanguageSwitcher';
import { RiChatAiFill } from "react-icons/ri";
import { startTour } from '../../utils/Tour';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loaderState, setLoaderState] = useState(false);

  // Check if user is authenticated (you might want to use Redux state instead)
  const isAuthenticated = localStorage.getItem('access_token') || false;
  
  // Check if we're on a landing page (no auth required)
  const isLandingPage = location.pathname === '/' || 
                        location.pathname === '/login' || 
                        location.pathname === '/sign-up' ||
                        location.pathname === '/pricing' ||
                        location.pathname === '/about-us' ||
                        location.pathname === '/contact-us';

  // Navigation items for authenticated users (from Header.jsx)
  const navigationItems = [
    {
      id: 'nav-dashboard',
      label: 'Dashboard',
      path: '/dashboard-home',
      icon: 'LayoutDashboard',
      description: 'Overview and quick start'
    },
    {
      id: 'nav-sessions-planner',
      label: 'Planner',
      path: '/upcoming-sessions-planner',
      icon: 'CalendarDays',
      description: 'Schedule upcoming sessions'
    },
    {
      id: 'nav-ai-interview',
      label: 'AI Interview',
      path: '/interview-setup-configuration',
      icon: 'Play',
      description: 'Interview preparation'
    },
    {
      id: 'nav-chat-interview',
      label: 'Chat Interview',
      path: '/chat-interview',
      icon: 'BotMessageSquare',
      description: 'Interview session with AI'
    },
    {
      id: 'nav-questions-ai',
      label: 'Questions AI',
      path: '/generate-questions',
      icon: 'List',
      description: 'Generate interview questions'
    },
    {
      id: 'nav-library',
      label: 'Library',
      path: '/practice-library-resources',
      icon: 'BookOpen',
      description: 'Resources and materials'
    },
    {
      id: 'nav-analytics',
      label: 'Analytics',
      path: '/progress-analytics-insights',
      icon: 'BarChart3',
      description: 'Performance insights'
    },
    {
      id: 'nav-resume-ai',
      label: 'Resume AI',
      path: '/resume-builder',
      icon: 'FileBox',
      description: 'Resume AI builder'
    },
  ];

  const secondaryItems = [
    {
      id: 'menu-settings',
      label: 'Settings',
      path: '/setting',
      icon: 'Settings'
    },
    {
      id: 'menu-help',
      label: 'Help',
      path: '/help',
      icon: 'HelpCircle'
    },
    {
      id: 'menu-take-tour',
      label: "Take Tour",
      icon: "HelpCircle",
      action: "tour"
    }
  ];

  const navLinks = [
    { href: "pricing", label: "Pricing" },
    { href: "about-us", label: "About us" },
    { href: "contact-us", label: "Contact us" },
  ];

  const isActivePath = (path) => {
    if (path === '/dashboard-home') {
      return location?.pathname === '/' || location?.pathname === '/dashboard-home';
    }
    return location?.pathname?.startsWith(path);
  };

  const handleNavigation = (path) => {
    setLoaderState(true);
    setTimeout(() => {
      navigate(path);
      setLoaderState(false);
    }, 1000);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    if (isAuthenticated && !localStorage.getItem("tour_done")) {
      startTour();
      localStorage.setItem("tour_done", "true");
    }
  }, [isAuthenticated]);

  return (
    <>
      {/* Loading Overlay */}
      {loaderState && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center" style={{zIndex: 100000000}}>
          <div className="loader-container flex flex-col items-center justify-center">
            <span className="loader"></span>
            <p className="text-gray-300 mt-2 text-sm">Please wait a moment...</p>
          </div>
        </div>
      )}

      {/* Navbar - Main Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 w-full py-1 px-4 sm:px-6 backdrop-blur-xl bg-black/20 border-b border-white/10 z-50"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to={isAuthenticated ? "/dashboard-home" : "/"} className="flex min-w-0 items-center space-x-2 sm:space-x-3">
            <img src={"/GenieAI_2.png"} alt="InterviewGenieAI" className="w-12 h-12 sm:w-14 sm:h-14 md:w-24 md:h-16" />
            <div className="hidden min-[420px]:flex flex-col">
              <h1 className="text-sm sm:text-base md:text-lg font-semibold text-white">InterviewGenieAI</h1>
              <span className="text-xs text-gray-400">Professional Assistant</span>
            </div>
          </Link>

          {/* Desktop Navigation - Conditionally render based on authentication */}
          {isAuthenticated && !isLandingPage ? (
            // Authenticated User Navigation
            <div className="hidden md:flex items-center gap-2">
              {navigationItems?.slice(0, 4).map((item, index) => (
                <button
                  id={item.id}
                  key={index}
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    relative flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium
                    transition-all duration-300 ease-smooth group
                    ${isActivePath(item?.path)
                      ? 'text-purple-300 transform bg-purple-500/10 border border-purple-500/20 shadow-md shadow-purple-500/20'
                      : 'text-gray-300 transform hover:scale-105 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  {item?.label === "Interview" ? (
                    <RiChatAiFill size={18} className={`transition-colors duration-300 ${
                      isActivePath(item?.path) ? 'text-purple-300' : 'group-hover:text-white'
                    }`}/>
                  ) : (
                    <Icon
                      name={item?.icon}
                      size={18}
                      className={`transition-colors duration-300 ${
                        isActivePath(item?.path) ? 'text-purple-300' : 'group-hover:text-white'
                      }`}
                    />
                  )}
                  <span>{item?.label}</span>
                  {isActivePath(item?.path) && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-purple-400 rounded-full shadow-[0_0_8px_rgba(192,132,252,0.8)]" />
                  )}
                </button>
              ))}
              
              {/* More Menu for authenticated users */}
              <div className="relative group ml-2">
                <Button variant="ghost" size="sm" iconName="MoreHorizontal" className="text-gray-300 hover:text-white">
                  More
                </Button>
                <div className="absolute right-0 top-full mt-2 w-48 bg-black/80 backdrop-blur-xl rounded-lg border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-smooth">
                  <div className="p-2">
                    {/* Show remaining navigation items */}
                    {navigationItems?.slice(4).map((item) => (
                      <button
                        id={item.id}
                        key={item?.path}
                        onClick={() => handleNavigation(item?.path)}
                        className="flex items-center space-x-3 w-full px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-md transition-colors duration-150"
                      >
                        <Icon name={item?.icon} size={16} />
                        <span>{item?.label}</span>
                      </button>
                    ))}
                    
                    <div className="border-t border-white/10 my-2"></div>
                    
                    {/* Secondary items */}
                    {secondaryItems?.map((item) => (
                      <button
                        id={item.id}
                        key={item?.path || item.action}
                        onClick={() => {
                          if (item.action === "tour") {
                            localStorage.removeItem("tour_done");
                            startTour();
                          } else {
                            handleNavigation(item?.path);
                          }
                        }}
                        className="flex items-center space-x-3 w-full px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-md transition-colors duration-150"
                      >
                        <Icon name={item?.icon} size={16} />
                        <span>{item?.label}</span>
                      </button>
                    ))}
                    
                    <div className="border-t border-white/10 my-2"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 w-full px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors duration-150"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Unauthenticated/Landing Page Navigation
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm hover:text-[#6C63FF] transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          {/* Desktop Auth Buttons - Conditionally render */}
          {!isAuthenticated || isLandingPage ? (
            <div className="hidden md:flex items-center gap-4">
              <LanguageSwitcher compact className="shrink-0" />
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/login')}
                className="text-sm hover:text-[#6C63FF] transition-colors"
              >
                Login
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/sign-up")}
                style={{
                  boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
                }}
                className="px-3 py-2 group rounded-xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-2 relative overflow-hidden"
              >
                <div 
                  className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px] pointer-events-none"
                />
                <span className="relative z-10">Get Started</span>
                <ArrowRight className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={18} />
              </motion.button>
            </div>
          ) : (
            // Authenticated user profile/actions (if needed)
            <div className="hidden md:flex items-center gap-4">
              <LanguageSwitcher compact className="shrink-0" />
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                  <span className="text-black font-medium">{JSON.parse(localStorage.getItem('user'))?.first_name[0]+JSON.parse(localStorage.getItem('user'))?.last_name[0] }</span>
                </div>
                <span>{JSON.parse(localStorage.getItem('user'))?.first_name+" "+JSON.parse(localStorage.getItem('user'))?.last_name }</span>
              </div>
            </div>
          )}

          {/* Mobile Language + Menu Button */}
          <div className="md:hidden flex items-center gap-2 shrink-0">
            <LanguageSwitcher compact className="!px-1.5 !py-1 scale-95 origin-right" />
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X size={24} className="text-white" />
              ) : (
                <Menu size={24} className="text-white" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-16 z-40 md:hidden"
          >
            <div className="absolute inset-0 bg-black/90 backdrop-blur-xl">
              <div className="flex flex-col items-center justify-center h-full px-6 space-y-8">
                <div className="w-full max-w-sm flex justify-center">
                  <LanguageSwitcher className="w-full justify-center rounded-2xl px-4 py-3" />
                </div>
                {isAuthenticated && !isLandingPage ? (
                  // Authenticated Mobile Menu
                  <>
                    {/* Navigation Items */}
                    <div className="flex flex-col items-center space-y-4 w-full max-w-sm">
                      {navigationItems?.map((item) => (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`
                            flex items-center space-x-3 w-full px-4 py-3 rounded-lg text-left
                            transition-all duration-200 ease-smooth
                            ${isActivePath(item?.path)
                              ? 'text-purple-300 bg-purple-500/10 border border-purple-500/20' 
                              : 'text-gray-300 hover:text-white hover:bg-white/5'
                            }
                          `}
                        >
                          {item?.label === "Interview" ? (
                            <RiChatAiFill size={20} className={isActivePath(item?.path) ? 'text-purple-300' : ''} />
                          ) : (
                            <Icon 
                              name={item?.icon} 
                              size={20} 
                              className={isActivePath(item?.path) ? 'text-purple-300' : ''} 
                            />
                          )}
                          <div>
                            <div className="font-medium">{item?.label}</div>
                            <div className="text-xs text-gray-400">{item?.description}</div>
                          </div>
                        </button>
                      ))}
                      
                      {/* Secondary Items */}
                      <div className="border-t border-white/10 pt-4 mt-4 w-full">
                        {secondaryItems?.map((item) => (
                          <button
                            key={item?.path || item.action}
                            onClick={() => {
                              if (item.action === "tour") {
                                localStorage.removeItem("tour_done");
                                startTour();
                                setIsMobileMenuOpen(false);
                              } else {
                                handleNavigation(item?.path);
                              }
                            }}
                            className="flex items-center space-x-3 w-full px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors duration-150"
                          >
                            <Icon name={item?.icon} size={20} />
                            <span className="font-medium">{item?.label}</span>
                          </button>
                        ))}
                      </div>

                      {/* Logout Button */}
                      <div className="border-t border-white/10 pt-4 mt-4 w-full">
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-3 w-full px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors duration-150"
                        >
                          <Icon name="LogOut" size={20} />
                          <span className="font-medium">Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Unauthenticated Mobile Menu
                  <>
                    {/* Navigation Links */}
                    <div className="flex flex-col items-center space-y-6 w-full max-w-sm">
                      {navLinks.map((link) => (
                        <motion.a
                          key={link.label}
                          href={link.href}
                          className="text-lg font-medium text-white hover:text-[#6C63FF] transition-colors py-2 w-full text-center border-b border-white/10"
                          onClick={() => setIsMobileMenuOpen(false)}
                          whileHover={{ scale: 1.05 }}
                        >
                          {link.label}
                        </motion.a>
                      ))}
                    </div>

                    {/* Mobile Auth Buttons */}
                    <div className="flex flex-col space-y-4 w-full max-w-sm pt-8 border-t border-white/10">
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          navigate('/login');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full py-3 rounded-lg bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors"
                      >
                        Login
                      </motion.button>
                      
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          navigate("/sign-up");
                          setIsMobileMenuOpen(false);
                        }}
                        style={{
                          boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
                        }}
                        className="w-full py-3 group rounded-lg bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-2 relative overflow-hidden"
                      >
                        <div 
                          className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px] pointer-events-none"
                        />
                        <span className="relative z-10">Get Started</span>
                        <ArrowRight className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={18} />
                      </motion.button>
                    </div>
                  </>
                )}

                {/* Close Menu Instruction */}
                <p className="text-sm text-gray-400 mt-8">
                  Tap anywhere outside to close
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-30 md:hidden"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Mobile Bottom Navigation - Only for authenticated users */}
      {isAuthenticated && !isLandingPage && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-black/80 backdrop-blur-xl border-t border-white/10">
          <div className="grid grid-cols-5 gap-1 py-2 px-2">
            {navigationItems.slice(0, 4).map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  flex flex-col items-center justify-center space-y-1 px-2 py-2 rounded-lg
                  transition-all duration-200 ease-smooth
                  ${isActivePath(item?.path)
                    ? 'text-purple-300 bg-purple-500/10' 
                    : 'text-gray-300 hover:text-white'
                  }
                `}
              >
                {item?.label === "Interview" ? (
                  <RiChatAiFill 
                    size={20} 
                    className={`transition-colors duration-200 ${
                      isActivePath(item?.path) ? 'text-purple-300' : ''
                    }`}
                  />
                ) : (
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    className={`transition-colors duration-200 ${
                      isActivePath(item?.path) ? 'text-purple-300' : ''
                    }`}
                  />
                )}
                <span className="text-[10px] font-medium leading-tight">{item?.label}</span>
                {isActivePath(item?.path) && (
                  <div className="w-1 h-1 bg-purple-400 rounded-full" />
                )}
              </button>
            ))}
            {/* More button in bottom nav */}
            <button
              onClick={toggleMobileMenu}
              className="flex flex-col items-center justify-center space-y-1 px-2 py-2 rounded-lg text-gray-300 hover:text-white transition-all duration-200"
            >
              <Icon name="MoreHorizontal" size={20} />
              <span className="text-[10px] font-medium leading-tight">More</span>
            </button>
          </div>
        </nav>
      )}
    </>
  );
};

export default Navbar;
